import React, { useState } from 'react';
import { Box, Typography, Paper, Stepper, Step, StepLabel, Button } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import FileUpload from '../components/FileUpload';
import WorksheetSelection from '../components/WorksheetSelection';
import ColumnMapping from '../components/ColumnMapping';
import Analysis from '../components/Analysis';
import NexusRulesEditor from '../components/NexusRulesEditor';
import SlideTransition from '../components/SlideTransition';
import { AnalysisResult, UploadResponse } from '../types';

// Steps in the process
const steps = ['Upload File', 'Select Worksheets', 'Map Columns', 'Analysis'];

const MainApp: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [fileData, setFileData] = useState<UploadResponse | null>(null);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<string, any>>({});
  const [currentSheetIndex, setCurrentSheetIndex] = useState(0);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [showNexusRules, setShowNexusRules] = useState(false);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileUpload = (data: UploadResponse) => {
    setFileData(data);
    
    // If CSV, automatically select the sheet and move to column mapping
    if (data.fileType === 'csv') {
      setSelectedSheets(data.sheets);
      setActiveStep(2); // Skip worksheet selection
    } else {
      handleNext();
    }
  };

  const handleSheetSelection = (sheets: string[]) => {
    setSelectedSheets(sheets);
    setCurrentSheetIndex(0);
    handleNext();
  };

  const handleColumnMapping = (sheetName: string, mapping: any) => {
    setColumnMappings((prev) => ({
      ...prev,
      [sheetName]: mapping,
    }));

    if (currentSheetIndex < selectedSheets.length - 1) {
      setCurrentSheetIndex(currentSheetIndex + 1);
    } else {
      handleNext();
    }
  };

  const handleAnalysisComplete = (result: AnalysisResult) => {
    setAnalysisResult(result);
  };

  const toggleNexusRules = () => {
    setShowNexusRules(!showNexusRules);
  };

  const handleReset = () => {
    // Reset all state
    setActiveStep(0);
    setFileData(null);
    setSelectedSheets([]);
    setColumnMappings({});
    setCurrentSheetIndex(0);
    setAnalysisResult(null);
  };

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h1" component="h1" align="center" gutterBottom>
        Nexus-on Top
      </Typography>
      
      {showNexusRules ? (
        <NexusRulesEditor onClose={toggleNexusRules} />
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Paper sx={{ p: 3 }}>
            {activeStep === 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h2">
                    Upload Your Data File
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={toggleNexusRules}
                  >
                    View Nexus Rules
                  </Button>
                </Box>
                <FileUpload onUploadComplete={handleFileUpload} />
              </>
            )}

            {activeStep === 1 && fileData && (
              <WorksheetSelection 
                sheets={fileData.sheets} 
                onSheetSelect={handleSheetSelection}
                onBack={handleBack}
              />
            )}

            {activeStep === 2 && fileData && (
              <ColumnMapping
                filename={fileData.filename}
                sheetName={selectedSheets[currentSheetIndex]}
                isShopQL={fileData.isShopQL}
                onComplete={handleColumnMapping}
                onBack={handleBack}
                currentSheet={currentSheetIndex + 1}
                totalSheets={selectedSheets.length}
              />
            )}

            {activeStep === 3 && fileData && (
              <SlideTransition>
                <Analysis
                  filename={fileData.filename}
                  sheets={selectedSheets}
                  columnMappings={columnMappings}
                  onAnalysisComplete={handleAnalysisComplete}
                  onBack={handleBack}
                  onReset={handleReset}
                />
              </SlideTransition>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MainApp;